/**
 * Which theme the app is in, and remembering it.
 *
 * P0 was dark-first with a *sunlight* mode — not a light theme, but maximum contrast for a
 * phone in 38°C glare: pure black on pure white, 3px borders, enlarged type. P1 reverses
 * that (PRD-P1 §5.1): light is the default and dark is the alternate, because the primary
 * complaint about the P0 UI was that a dark-only app does not read as a product an organiser
 * would switch to.
 *
 * ## Why this is a module and not two lines in `App`
 *
 * The preference has to survive a reload, and the reload is the interesting part. P0 held it
 * in `useState` and lost it every time, which was tolerable when the toggle was one tap from
 * every screen. It is not tolerable now: dark mode is a *preference*, and a preference the
 * app forgets is one the scorer re-sets forty times.
 *
 * Reading it back has to happen before first paint or the screen flashes light and then goes
 * dark, so `applyTheme` writes the attribute on `<html>` directly rather than waiting for
 * React to render — see `main.tsx`.
 *
 * Storage failures are non-events. `localStore()` already falls back to memory when Safari
 * refuses the probe, and a forgotten theme is the smallest possible loss.
 */
import { localStore, type Store } from "./localStore.ts";

export type Theme = "light" | "dark";

const KEY = "sportsos.theme";

/** Light unless something says otherwise. PRD-P1 §5.1: light-first. */
export const DEFAULT_THEME: Theme = "light";

/**
 * The stored preference, or the default.
 *
 * Anything unrecognised is the default rather than an error — the value could be from an
 * older build that wrote `"sunlight"`, and a scorer should not have to clear site data to
 * get a working screen back.
 */
export function readTheme(store: Store = localStore()): Theme {
  const stored = store.getItem(KEY);
  return stored === "light" || stored === "dark" ? stored : DEFAULT_THEME;
}

export function writeTheme(theme: Theme, store: Store = localStore()): void {
  try {
    store.setItem(KEY, theme);
  } catch {
    // A quota error or a private window. The theme is a preference; losing it costs a tap.
  }
}

export const otherTheme = (theme: Theme): Theme => (theme === "dark" ? "light" : "dark");

/**
 * Put the theme on `<html>`.
 *
 * Light writes **no attribute at all**, matching the stylesheet: `:root` carries light and
 * `[data-theme="dark"]` overrides it. Writing `data-theme="light"` would work today and would
 * silently stop working the moment somebody adds a `[data-theme]`-scoped rule.
 */
export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  if (theme === "dark") {
    root.dataset["theme"] = "dark";
    root.classList.add("ds-on-deep");
    root.classList.remove("ds-on-light");
  } else {
    delete root.dataset["theme"];
    root.classList.remove("ds-on-deep");
    root.classList.add("ds-on-light");
  }
}

/**
 * What the installed app paints behind the status bar, per theme.
 *
 * `--ds-navy-950` and `--ds-neutral-50` from `tokens.css`, as literals — the same two values
 * `index.html` hard-codes into its `<meta name="theme-color">` pair, and for the same reason:
 * nothing here has a document to read a custom property from.
 */
const THEME_COLOR: Record<Theme, string> = { light: "#f9f9f9", dark: "#051533" };

/**
 * Point every `theme-color` meta at the theme the app is actually in.
 *
 * **Separate from `applyTheme`, deliberately.** That function takes an element and
 * `theme.test.ts` calls it with a hand-rolled object that has a `dataset` and a `classList`
 * and no owning document. Reaching for `document` from inside it would break that test to
 * save a line here.
 *
 * ## Why it overwrites *both* metas rather than picking one
 *
 * The two in `index.html` are scoped by `prefers-color-scheme`, which answers a different
 * question from the one this app asks: the theme is a stored preference, so a scorer on a
 * light-mode phone who chose dark has the OS saying one thing and the app doing another.
 * Writing the resolved colour into both makes the media queries moot from that point on,
 * which is the correct outcome — the app's own theme is the ground truth once it is running.
 * The queries still earn their place in the window before this runs.
 *
 * Silent when there are no metas to update. This is chrome colour; a document that does not
 * have them is a test, and it should not be this function's business.
 */
export function applyThemeColor(theme: Theme, doc: Document = document): void {
  const metas = doc.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  for (const meta of metas) meta.content = THEME_COLOR[theme];
}
